import { F360UnitDate, type IF360Date } from "../date/F360Date";
import dayjs from "dayjs";
// import * as dayjs from 'dayjs';
// import * as isLeapYear from "dayjs/plugin/isLeapYear";
import isBetween from "dayjs/plugin/isBetween";
import "dayjs/locale/pt-br";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { injectable } from "inversify";
import "reflect-metadata";
import minMax from "dayjs/plugin/minMax";

@injectable()
export class F360DayJs implements IF360Date {
  constructor() {
    this.setLocale("pt-br");
    this.setPlugins(isBetween);
    this.setPlugins(utc);
    this.setPlugins(timezone);
    this.setPlugins(minMax);
  }

  getTotalDays(
    date: string | Date,
    compareDate: string | Date,
    unit?: F360UnitDate
  ): number {
    const rangeInicio = dayjs(date);
    const rangeFim = dayjs(compareDate);

    return rangeFim.diff(rangeInicio, unit);
  }

  getDiff(
    start: Date | string,
    end: Date | string,
    from?: F360UnitDate
  ): number {
    const date1 = dayjs(start);

    return date1.diff(end, this.tryFormat(from || F360UnitDate.Millisecond));
  }

  isBefore(date: string | Date, compareDate: string | Date): boolean {
    return dayjs(date).isBefore(compareDate);
  }

  getMinimum(dateRange: Date[] | string[]): Date {
    const date = dayjs.min(dateRange.map((item) => dayjs(item)));

    return dayjs(date).toDate();
  }

  getNewDate(date: string | Date | null) {
    if (!date) return new Date(new Date().setHours(0, 0, 0, 0));

    const dateTime = new Date(date);
    dateTime.setHours(0, 0, 0, 0);

    return dateTime;
  }

  isSame(date: string | Date, compare: string | Date): boolean {
    return dayjs(date).isSame(compare);
  }

  getFromTimezone(date: string | Date): Date {
    return dayjs
      .tz(date, Intl.DateTimeFormat().resolvedOptions().timeZone)
      .toDate();
  }

  getMonth(date: string | Date): number {
    return dayjs(date).get("month");
  }

  getDateFromMonth(month: number): Date {
    return dayjs().month(month).toDate();
  }

  now(): Date {
    return dayjs(new Date()).toDate();
  }

  handleFormat(format: F360UnitDate | undefined) {
    const formats: Partial<Record<F360UnitDate, dayjs.ManipulateType>> = {
      [F360UnitDate.Day]: "day",
      [F360UnitDate.Month]: "M",
      [F360UnitDate.Days]: "days",
      [F360UnitDate.Year]: "y",
      [F360UnitDate.week]: "week",
      [F360UnitDate.Hour]: "h",
      [F360UnitDate.Millisecond]: "ms",
      [F360UnitDate.Second]: "s",
      [F360UnitDate.Minute]: "m"
    };

    if (!format) return undefined;

    return formats[format];
  }

  private tryFormat(
    format: F360UnitDate | undefined,
    fallback = F360UnitDate.Date
  ) {
    return this.handleFormat(format) || fallback;
  }

  add(date: Date | string, value: number, manipulateType?: F360UnitDate) {
    const newDate = dayjs(date).add(value, this.handleFormat(manipulateType));

    return new Date(newDate.toJSON());
  }

  subtract(
    date: string | Date,
    value: number,
    manipulateType?: F360UnitDate
  ): Date {
    const newDate = dayjs(date).subtract(
      value,
      this.handleFormat(manipulateType)
    );

    return new Date(newDate.toJSON());
  }

  startOf(date: string | Date, OpUnitType: F360UnitDate): string | Date {
    const newDate = dayjs(date).startOf(this.tryFormat(OpUnitType));

    return new Date(newDate.toJSON());
  }

  endOf(date: string | Date, OpUnitType: F360UnitDate): string | Date {
    const newDate = dayjs(date).endOf(this.tryFormat(OpUnitType));

    return new Date(newDate.toJSON());
  }

  format(date: string, format: string, formatFrom?: string): string {
    return dayjs(date, formatFrom).format(format);
  }

  isValid(date: string | Date): boolean {
    return dayjs(date).isValid();
  }

  setLocale(locale: string): void {
    dayjs.locale(locale);
  }

  isBetween(
    date: string | Date,
    compareStart: string | Date,
    compareEnd: string | Date,
    OpUnitType: F360UnitDate
  ): boolean {
    return dayjs(date).isBetween(compareStart, dayjs(compareEnd), OpUnitType);
  }

  setPlugins(plugin: unknown): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dayjs.extend(plugin as any);
  }

  getHour(date: Date): string {
    return dayjs(date).format("HH:mm");
  }
}
